import { useMemo, useState } from 'react';
import { Col, Row } from 'antd';
import { useLocation } from 'react-router-dom';
import { Button } from 'components';
import { DeleteIcon } from 'icons';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { getShoppingCenters } from 'hooks/useUser';
import { getShops } from 'hooks/useShops';
import { useApi } from 'hooks/useApi';
import MisteryMisteryReportsApi from 'libs/api/mistery/misteryReports';
import MisteryReportAdapter from './misteryReport.adapter';
import LinkWithParams from 'components/linkWithParams';
import Helpers from 'helpers';
import UIHelper from 'helpers/UIHelper';
import { FilePdfOutlined } from '@ant-design/icons';
import { useFilter } from '../../../../hooks/useFilter';

const api = new MisteryMisteryReportsApi();

const useMisteryReports = (isMainPage = true) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { filterData } = useFilter();
  const [pdfLoadingId, setPdfLoadingId] = useState(null);
  const {
    data,
    onChangeTableFilter,
    loading,
    updateInProcess,
    handleDownloadExcel,
    onDelete,
  } = useApi({
    resourceKey: 'mistery_reports',
    api: api,
    isMainPage,
    adapter: MisteryReportAdapter,
  });

  const handleDownloadPdf = async (id, fileName = 'report.pdf') => {
    setPdfLoadingId(id);
    try {
      const response = await api.getPDF(id);
      Helpers.downloadFile(response, fileName);
    } catch (e) {
      UIHelper.serverError();
    } finally {
      setPdfLoadingId(null);
    }
  };

  const statusType = useMemo(
    () => ({
      pending: t('Non letto'),
      readed: t('Letto'),
    }),
    [t],
  );

  const columns = useMemo(() => {
    return [
      {
        title: t('Village'),
        dataIndex: 'shopping_centers.title',
        fixed: 'left',
        sorter: () => {},
      },
      {
        title: t('Insegna'),
        dataIndex: 'companies.title',
        sorter: () => {},
      },
      {
        title: t('Report Date'),
        dataIndex: 'file_date',
        sorter: () => {},
      },
      {
        title: t('File'),
        dataIndex: 'file',
        render: (data, row) => {
          return (
            <Button
              onClick={() => handleDownloadPdf(row.id, row.file)}
              type={'green'}
              icon={<FilePdfOutlined />}
              loading={pdfLoadingId === row.id}
            />
          );
        },
      },
      {
        title: t('Status'),
        dataIndex: 'status',
        sorter: () => {},
        render: (data) => {
          return statusType[data];
        },
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 80,
        fixed: 'right',
        render: (data, row) =>
          row.can_destroy && (
            <Row
              gutter={16}
              justify={'center'}
              onClick={(e) => e.stopPropagation()}
            >
              <Col>
                <LinkWithParams to={`${pathname}/${row.id}/delete`}>
                  <Button type={'red'} icon={<DeleteIcon />} />
                </LinkWithParams>
              </Col>
            </Row>
          ),
      },
    ];
  }, [pathname, pdfLoadingId]);
  const filters = useMemo(
    () => [
      {
        title: t('Outlet Village'),
        type: 'select',
        name: 'by_shopping_center',
        values: filterData?.shopping_centers?.map((shopping_center) => ({
          title: shopping_center.title,
          value: shopping_center.value,
        })),
      },
      {
        title: t('Insegna'),
        type: 'select',
        name: 'by_company',
        isSearch: true,
        values: filterData?.companies?.map((company) => ({
          title: company.title,
          value: company.value,
        })),
        inactiveValues: filterData?.inactive_companies?.map((company) => ({
          title: company.title,
          value: company.value,
        })),
      },
      {
        title: t('Insegne attive/disattive'),
        type: 'select',
        name: 'by_company_active',
        isSearch: true,
        values: [
          {
            title: 'Attive',
            value: 1,
          },
          {
            title: 'Disattive',
            value: 0,
          },
        ],
      },
    ],
    [t, filterData],
  );

  return {
    columns,
    data,
    filters,
    onChangeTableFilter,
    loading,
    handleDownloadExcel,
    onDelete,
    updateInProcess,
  };
};

export default useMisteryReports;
